import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { Auth } from 'aws-amplify';
import activitiesReducer from '../reducers/activityReducer';
import assessmentsReducer, { saveUnsavedChanges as assessmentsSave } from '../reducers/assessmentReducer';
import authenticationReducer, { refresh, setRefreshing, forceLogin } from '../reducers/authenticationReducer';
import catalogsReducer from '../reducers/catalogsReducer';
import findingsReducer from '../reducers/findingsReducer';
import frameworksReducer, { saveUnsavedChanges as frameworksSave } from '../reducers/frameworkReducer';
import organizationsReducer from '../reducers/organizationReducer';

const saveFunctions = {
  assessments: assessmentsSave,
  frameworks: frameworksSave,
};

let autosaveTimer;
const debounceTime = 60000; // 1 minute
let deferredActions = [];

const hasUnsavedChanges = (part, action) => {
  let rval = false;
  if (part === 'assessments') {
    rval = action.startsWith('update');
  } else if (part === 'frameworks') {
    rval = action === 'updateGroups';
  }
  return rval;
};

const saveDebounce = (store, prefix) => {
  if (autosaveTimer) {
    clearTimeout(autosaveTimer);
  }

  autosaveTimer = setTimeout(() => {
    store.dispatch(saveFunctions[prefix](''));
  }, debounceTime);
};

const autosave = (store) => (next) => (action) => {
  const parts = action.type.split('/');
  if (hasUnsavedChanges(parts[0], parts[1])) {
    saveDebounce(store, parts[0]);
  }
  return next(action);
};

const refresher = (store) => (next) => async (action) => {
  // authentication slice events don't update expiration times
  if (action.type.startsWith('authentication')) {
    return next(action);
  }
  const { expires, loginExpires, refreshing } = store.getState().authentication;
  // some action started refresh token process, so store actions to dispatch after refresh
  if (refreshing) {
    deferredActions.push(action);
    return null;
  }
  const now = Math.floor(Date.now() / 1000);
  // set the maximum time for inactivity, 3 hours
  let expiryData = { loginExpires: now + 3 * 3600 };
  const expired = !!expires && expires < now;
  const loginExpired = !!loginExpires && loginExpires < now;
  // user's been inactive too long => force user to login again
  if (loginExpired) {
    store.dispatch(forceLogin());
    return null;
  }
  // refresh expired access token
  if (expired) {
    try {
      store.dispatch(setRefreshing(true));
      const user = await Auth.currentSession();
      const expiry = user.accessToken.payload.exp;
      expiryData = {
        ...expiryData,
        refreshing: false,
        expires: expiry,
      };
      // dispatch actions deferred during token refresh
      deferredActions.forEach((item) => store.dispatch(item));
      deferredActions = [];
    } catch (error) {
      store.dispatch(forceLogin());
      return null;
    }
  }
  store.dispatch(refresh(expiryData));
  return next(action);
};

const rootReducer = combineReducers({
  activities: activitiesReducer,
  assessments: assessmentsReducer,
  organizations: organizationsReducer,
  authentication: authenticationReducer,
  catalogs: catalogsReducer,
  findings: findingsReducer,
  frameworks: frameworksReducer,
});

const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) => getDefaultMiddleware().concat(autosave, refresher),
});

export default store;
